import { useQuery } from "@stadium/services/apollo-client";
import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useFlags } from "@stadium/services/launch-darkly/use-flags";

import { isGlobalCountry } from "@stadium/helpers/country/utils";
import { withFeatureFlags } from "@stadium/services/launch-darkly";

import AccordionAllCollapsable from "@stadium/ui/src/components/accordion/accordion-all-collapsable";
import Loaders from "@stadium/ui/src/components/loaders/loaders";

import { InternalButton } from "@sharedComponents/buttons";
import { UNKNOWN_BRAND_NAME } from "@stadium/constants/brands";
import {
  getCartItemsCount,
  getCartOrder,
  getCartOrderFetching,
  getCartOrderId,
  getCartOrderLineItems,
  getCartOrderMedleySets,
  getCartOrderProductSets,
  getIsCartUpdating,
} from "@store/selectors/cart";

import { updateCart } from "@slices/cart";
import { getCountry } from "@store/selectors/country";
import {
  getStoreCurrencyCode,
  getStoreIsOrderFetching,
  getStoreNumber,
} from "@store/selectors/store";

import {
  computeExternalSourceIds,
  generateShopsItems,
  getUpdatedLineItemFromVariants,
  getVendorName,
} from "@helpers/orders/cart";
import { isByoProduct, isCollectionTypeProduct } from "@helpers/products/types";

import { useStoreContext } from "@components/store/provider";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useSendItemsContext } from "@contexts/send-items-context";

import { fetchVariantsList } from "@graphql/queries/stores/products/fetch-variant-list";

import LineItemCard from "@sharedComponents/cart/line-item-card";

import useConfirmationModal from "@hooks/cart/use-confirmation-modal";
import useEmptyCart from "@hooks/cart/use-empty-cart";
import usePopulateItems from "@hooks/cart/use-populate-items";
import usePopulateLocalItems from "@hooks/cart/use-populate-local-items";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import { variantListLimitQueryFieldsFlag } from "@stadium/services/launch-darkly/constants";
import { removeItemsConfirmation } from "@constants/confirmation-modal";
import { byo } from "@constants/products/detail";
import { getMenuRoute } from "@constants/routes";

import { ITEM_PRODUCT_TYPES, ORDER_TYPES } from "@constants/orders/cart";
import { useCheckoutContext } from "@contexts/checkout-context";
import useBatchPopulateItems from "@hooks/cart/use-batch-populate-items";
import { EmptyBag } from "@sharedComponents/cart/common";
import { ANALYTICS_EVENT_ACTION_CLEARED } from "@stadium/services/analytics/constants/default";

import styles from "./main.module.scss";

const CartListing = ({ editable, className = "" }) => {
  const dispatch = useDispatch();

  const { isSendToOthers } = useSendItemsContext();
  const withAnalytics = useWithAnalytics();

  const { [variantListLimitQueryFieldsFlag]: variantListLimitQueryFieldsFlagEnabled } = useFlags();

  const cartOrder = useSelector(getCartOrder);
  const lineItems = useSelector(getCartOrderLineItems);
  const productSets = useSelector(getCartOrderProductSets);
  const medleySets = useSelector(getCartOrderMedleySets);
  const itemsCount = useSelector(getCartItemsCount);
  const orderId = useSelector(getCartOrderId);
  const orderFetching = useSelector(getCartOrderFetching);
  const storeNumber = useSelector(getStoreNumber);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const isStoreOrderFetching = useSelector(getStoreIsOrderFetching);
  const country = useSelector(getCountry);
  const { storeBasePath } = useStoreContext();
  const { isGuestOrPreviewMode } = usePreviewModeContext();
  const isCartUpdating = useSelector(getIsCartUpdating);

  const { cartItemsQuantity, orderCurrencyCode } = useCheckoutContext();

  const [isCartListingOpen, setIsCartListingOpen] = useState(false);
  const toggleCartListingAccordion = () => setIsCartListingOpen((prev) => !prev);

  const { externalSourceIds, catalogExternalStoreProductMappings } = useMemo(
    () => computeExternalSourceIds({ lineItems, productSets, medleySets }),
    [lineItems, productSets, medleySets],
  );

  const { volumeDiscountProductsVariables } = useSendItemsContext();

  const globalZone = isGlobalCountry(country);
  const variables = {
    globalZone,
    country: globalZone ? "" : country.iso,
    currency: storeCurrencyCode,
    storeNumber,
    catalogExternalStoreProductMappings,
    externalSourceIds,
    // required to fetch variant specific images for theme box products.
    isProductVariantsListRequired: !isEmpty(productSets),
    ...volumeDiscountProductsVariables,
    limitQueryFields: Boolean(variantListLimitQueryFieldsFlagEnabled) && !isSendToOthers,
  };
  const { toggleConfirmationModal, renderConfirmationModal } =
    useConfirmationModal(removeItemsConfirmation);

  const {
    emptyCart,
    emptyLocalCart,
    loading: emptyingCart,
  } = useEmptyCart({ successCb: toggleConfirmationModal });
  const {
    updateItemInCart,
    updateCollectionItemInCart,
    deleteItemFromCart,
    deleteCollectionItemFromCart,
    loading: shopProductLoading,
    updateByoItemFromCart,
    deleteByoItemFromCart,
  } = usePopulateItems();
  const {
    deleteItemFromCart: deleteBulkItemFromCart,
    updateItemInCart: updateBulkItemInCart,
    loading: bulkProductLoading,
  } = useBatchPopulateItems({
    type: ORDER_TYPES.organizer,
  });
  const loading = shopProductLoading || bulkProductLoading;

  const { updateItemInLocalCart, deleteItemFromLocalCart, resetLocalCart } =
    usePopulateLocalItems();
  const { data: variantData, loading: variantDataloading } = useQuery(fetchVariantsList, {
    variables,
    skip: !variables.externalSourceIds || isGuestOrPreviewMode,
    fetchPolicy: "cache-and-network",
  });

  const variantsMapping = useMemo(() => {
    if (variantDataloading || !variantData) {
      return {};
    } else {
      return groupBy(variantData?.variants, "externalId");
    }
  }, [variantDataloading, variantData]);

  //to update images of line items and product sets in redux store
  //cartOrder does needs to be added in the dependency array to avoid infinite loop
  useEffect(() => {
    if (!isEmpty(variantsMapping)) {
      const { lineItems, productSets } = cartOrder;

      const updatedLineItems = lineItems.map(getUpdatedItem);
      const updatedProductSets = productSets.map(getUpdatedItem);
      const updatedMedleySets = medleySets.map(getUpdatedItem);
      updatedMedleySets.forEach(
        (item) => (item.medleyComponents = item.medleyComponents.map(getUpdatedItem)),
      );
      dispatch(
        updateCart({
          ...cartOrder,
          lineItems: updatedLineItems,
          productSets: updatedProductSets,
          medleyItemSets: updatedMedleySets,
        }),
      );
    }
  }, [variantsMapping]);

  const disabled = emptyingCart || loading;

  const lineItemsMapping = useMemo(
    () => generateShopsItems(lineItems, variantsMapping, variantDataloading, isGuestOrPreviewMode),
    [lineItems, variantsMapping, variantDataloading, isGuestOrPreviewMode],
  );
  const productSetsMapping = useMemo(() => {
    let hash = {};
    productSets.forEach((item) => {
      if (variantsMapping?.[item.pcbId]) {
        const restaurantName = !variantDataloading
          ? getVendorName(variantsMapping[item.pcbId][0].product)
          : "";
        if (restaurantName) {
          hash[restaurantName] = hash[restaurantName] || [];
          hash[restaurantName].push({ ...item, restaurantName });
        }
      } else {
        const restaurantName = !variantDataloading ? UNKNOWN_BRAND_NAME : "";
        hash[restaurantName] = hash[restaurantName] || [];
        hash[restaurantName].push({ ...item, restaurantName });
      }
    });
    return hash;
  }, [productSets, variantsMapping, variantDataloading]);

  const medleySetMapping = useMemo(() => {
    if (!medleySets.length) return {};
    return { [UNKNOWN_BRAND_NAME]: medleySets };
  }, [medleySets]);

  const removeProductFromCart = ({ lineItem, intent }) => {
    const { productId, id, storeId, isBulkAvailable, productIds } = lineItem;
    if (isGuestOrPreviewMode) {
      if (isByoProduct(intent)) {
        return deleteItemFromLocalCart({ medleyItemSetId: id, storeId });
      }
      return deleteItemFromLocalCart({
        productId: isBulkAvailable ? productIds : productId,
        storeId,
      });
    }
    if (isByoProduct(intent)) return deleteByoItemFromCart({ medleyItemSetId: id, intent });

    if (isCollectionTypeProduct(intent)) {
      return deleteCollectionItemFromCart({ orderProductSetId: id, intent });
    }
    if (isBulkAvailable) {
      const payload = productIds.map((id) => ({
        product_id: id,
        quantity: 0,
        product_type: ITEM_PRODUCT_TYPES.product,
        store_id: storeId,
      }));
      deleteBulkItemFromCart(payload);
    } else {
      return deleteItemFromCart({ productId, intent, storeId });
    }
  };

  const onChangeQuantity = (payload, intent) => {
    if (isGuestOrPreviewMode) return updateItemInLocalCart({ ...payload, intent });
    if (isByoProduct(intent)) return updateByoItemFromCart({ ...payload, intent });
    if (isCollectionTypeProduct(intent)) {
      return updateCollectionItemInCart({ ...payload, intent });
    }
    if (payload?.isBulkAvailable) {
      const { productId, quantity, storePrice, price, storeId, vendorId } = payload;

      const updatedPayload = [
        {
          product_id: productId,
          quantity,
          storePrice,
          price,
          product_type: ITEM_PRODUCT_TYPES.product,
          store_id: storeId,
          vendor_id: vendorId,
        },
      ];
      return updateBulkItemInCart(updatedPayload);
    } else {
      return updateItemInCart({ ...payload, intent });
    }
  };

  const getUpdatedItem = (item) => {
    if (isGuestOrPreviewMode) return { ...item, masterVariant: item.product?.masterVariant };
    return getUpdatedLineItemFromVariants(item, variantsMapping, isSendToOthers);
  };

  const removeAllItemsHandler = (orderId) => {
    withAnalytics(undefined, {
      track: true,
      action: ANALYTICS_EVENT_ACTION_CLEARED,
      eventProperties: { total_items: itemsCount },
    })();
    if (isGuestOrPreviewMode) {
      emptyLocalCart();
      resetLocalCart();
    } else emptyCart(orderId);
  };

  const renderProducts = (lineItems, intent) => {
    return lineItems.map((item) => {
      const updatedItem = getUpdatedItem(item);
      return (
        <LineItemCard
          key={item.id}
          intent={intent}
          editable={editable}
          disabled={disabled}
          lineItem={updatedItem}
          removeProductFromCart={removeProductFromCart}
          onChangeQuantity={onChangeQuantity}
          getUpdatedItem={getUpdatedItem}
          orderCurrencyCode={orderCurrencyCode}
        />
      );
    });
  };

  const renderBrands = ({ intent, mapping }) => {
    const brands = Object.keys(mapping);

    return brands.map((brand) => {
      return (
        <dl className={styles.dl} key={brand.id}>
          <dt className={styles.brandHeading} key={brand}>
            {brand && `Ships from ${brand}`}:
          </dt>
          {renderProducts(mapping[brand], intent)}
        </dl>
      );
    });
  };

  const renderRemoveAllItemsCta = () =>
    editable && (
      <div className={styles.colAutoLink}>
        <InternalButton
          styleType="link"
          className={`${styles.removeLink} ${styles.withLH32}`}
          onClick={toggleConfirmationModal}
        >
          Remove All Items
        </InternalButton>
      </div>
    );

  if (
    (isStoreOrderFetching && !isGuestOrPreviewMode) ||
    orderFetching ||
    variantDataloading ||
    isCartUpdating
  )
    return (
      <div>
        <Loaders.PageLoader wrapperClass="pageLoaderwrap min-h-150" />
      </div>
    );

  if (itemsCount === 0) return <EmptyBag menuPath={getMenuRoute({ storeBasePath })} />;

  const renderCartListing = () => (
    <div className={className}>
      {renderBrands({ intent: byo, mapping: medleySetMapping })}
      {renderBrands({ intent: "collection", mapping: productSetsMapping })}
      {renderBrands({ intent: "default", mapping: lineItemsMapping })}
      {renderRemoveAllItemsCta()}
      {renderConfirmationModal({
        loading: emptyingCart,
        onConfirmHandler: () => removeAllItemsHandler(orderId),
      })}
    </div>
  );

  if (editable) return renderCartListing();

  return (
    <AccordionAllCollapsable
      id=""
      isOpen={isCartListingOpen}
      handleAccordianToggle={toggleCartListingAccordion}
      accordionCustomClass={styles.accordion}
      headerCustomClass={styles.accordionHeader}
      titleContent={
        <>
          Your Items ({cartItemsQuantity})
          <div className={styles.collapseIconBlock}>
            <span
              className={` ${styles.arrowIcon} ${
                isCartListingOpen ? styles.iconUp : styles.iconDown
              }`}
            />
          </div>
        </>
      }
      bodyContent={<div className={styles.collapseBlock}>{renderCartListing()}</div>}
    />
  );
};

export default withFeatureFlags(CartListing);
